import { useMoney } from "./useMoney";
import { snakeCase } from "lodash";

export function useGoogleDataLayer() {
    const { dollars } = useMoney();

    function clearPreviousEcommerceObject() {
        // FIXME(4e): `dataLayer === 'undefined'` always evaluates false (compares the
        // global to the string literal); the intended guard is `typeof dataLayer === 'undefined'`.
        if (dataLayer === "undefined") {
            return;
        }

        dataLayer.push({ ecommerce: null });
    }

    function pushAddToCartToDataLayer(attributes: any) {
        // FIXME(4e): `dataLayer === 'undefined'` always evaluates false; intended guard is `typeof`.
        if (dataLayer === "undefined") {
            return;
        }

        clearPreviousEcommerceObject();
        dataLayer.push({
            event: "add_to_cart",
            ecommerce: {
                items: [
                    {
                        item_id: attributes.product.id,
                        item_name: `${attributes.product.name} (${attributes.product.default_product_option.description})`,
                        currency: "USD",
                        item_list_id: snakeCase(attributes.source),
                        item_list_name: attributes.source,
                        item_variant: attributes.subscribe
                            ? "SUBSCRIPTION"
                            : "ONETIME",
                        price: dollars(
                            attributes.product.default_product_option.price,
                        ),
                        quantity: attributes.quantity,
                    },
                ],
            },
        });
    }

    function pushUpdateCartItemToDataLayer(attributes: any) {
        // FIXME(4e): `dataLayer === 'undefined'` always evaluates false; intended guard is `typeof`.
        if (dataLayer === "undefined") {
            return;
        }

        clearPreviousEcommerceObject();
        dataLayer.push({
            event: attributes.quantity > 0 ? "add_to_cart" : "remove_from_cart",
            ecommerce: {
                items: [
                    {
                        item_id: attributes.product.id,
                        item_name: `${attributes.product.name} (${attributes.productOption.description})`,
                        currency: "USD",
                        item_list_id: snakeCase(attributes.source),
                        item_list_name: attributes.source,
                        item_variant: attributes.subscribe
                            ? "SUBSCRIPTION"
                            : "ONETIME",
                        price: dollars(attributes.productOption.price),
                        quantity: Math.abs(attributes.quantity),
                    },
                ],
            },
        });
    }

    function pushViewItemListToDataLayer(attributes: any) {
        // FIXME(4e): `dataLayer === 'undefined'` always evaluates false; intended guard is `typeof`.
        if (dataLayer === "undefined" || !attributes.products.length) {
            return;
        }

        const items = [];

        for (const key in attributes.products) {
            const product = attributes.products[key];
            if (
                product &&
                Object.prototype.hasOwnProperty.call(
                    product,
                    "default_product_option",
                )
            ) {
                items.push({
                    item_id: product.id,
                    item_name: `${product.name} (${product.default_product_option.description})`,
                    currency: "USD",
                    item_list_id: snakeCase(attributes.source),
                    item_list_name: attributes.source,
                    item_variant: attributes.subscribe
                        ? "SUBSCRIPTION"
                        : "ONETIME",
                    price: dollars(product.default_product_option.price),
                    quantity: 1,
                });
            }
        }

        clearPreviousEcommerceObject();
        dataLayer.push({
            event: "view_item_list",
            ecommerce: {
                items: items,
            },
        });
    }

    function pushAddShippingInformationToDataLayer(attributes: any) {
        // FIXME(4e): `dataLayer === 'undefined'` always evaluates false; intended guard is `typeof`.
        if (dataLayer === "undefined" || !attributes.cartItems.length) {
            return;
        }

        clearPreviousEcommerceObject();
        dataLayer.push({
            event: "add_shipping_info",
            ecommerce: {
                items: cartItems(attributes),
                value: attributes.total,
                currency: "USD",
                shipping_tier: attributes.shippingTimeId,
            },
        });
    }

    function pushAddPaymentInformationToDataLayer(attributes: any) {
        // FIXME(4e): `dataLayer === 'undefined'` always evaluates false; intended guard is `typeof`.
        if (dataLayer === "undefined" || !attributes.cartItems.length) {
            return;
        }

        clearPreviousEcommerceObject();
        dataLayer.push({
            event: "add_payment_info",
            ecommerce: {
                items: cartItems(attributes),
                value: attributes.total,
                currency: "USD",
                payment_type: attributes.paymentType,
            },
        });
    }

    function cartItems(attributes: any) {
        const items = [];

        for (const key in attributes.cartItems) {
            const cartItem = attributes.cartItems[key];
            if (
                cartItem &&
                Object.prototype.hasOwnProperty.call(
                    cartItem,
                    "product_option",
                ) &&
                Object.prototype.hasOwnProperty.call(
                    cartItem.product_option,
                    "product",
                )
            ) {
                items.push({
                    item_id: cartItem.product_option.product.id,
                    item_name: `${cartItem.product_option.product.name} (${cartItem.product_option.description})`,
                    currency: "USD",
                    item_variant: cartItem.subscription_option_id
                        ? "SUBSCRIPTION"
                        : "ONETIME",
                    price: dollars(cartItem.product_option.price),
                    quantity: cartItem.quantity,
                });
            }
        }

        return items;
    }

    function pushVideoEventToDataLayer(attributes: any) {
        // FIXME(4e): `dataLayer === 'undefined'` always evaluates false; intended guard is `typeof`.
        if (dataLayer === "undefined") {
            return;
        }

        attributes.event = `video_${attributes.videoStatus}`;
        dataLayer.push(attributes);
    }

    return {
        pushAddToCartToDataLayer,
        pushUpdateCartItemToDataLayer,
        pushViewItemListToDataLayer,
        pushAddShippingInformationToDataLayer,
        pushAddPaymentInformationToDataLayer,
        pushVideoEventToDataLayer,
    };
}
